import React, { useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { ExportButton } from "../../Components/ExportButton";
import { GetSubscriptionReports } from "../../Api/subscription";
import ReportTab from "./components/ReportTab";
import UserTab from "./components/UserTab";
import FilterSidebar from "../../Components/FilterSidebar";
import Pagination from "../../Utils/Pagination";
import { FaPoundSign, FaCalendarAlt, FaCalendarCheck, FaUserEdit } from "react-icons/fa";

const Subscription = () => {
  const [type, setType] = useState("report");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    preset: "all",
    startDate: null,
    endDate: null,
    planType: "all",
    userOption: null,
  });
  const defaultFilters = useRef({
    search: "",
    preset: "all",
    startDate: null,
    endDate: null,
    planType: "all",
    userOption: null,
  });
  const [reportPage, setReportPage] = useState(0);
  const [reportRowsPerPage, setReportRowsPerPage] = useState(10);
  const [reportTotalCount, setReportTotalCount] = useState(0);
  const [reportTotals, setReportTotals] = useState({
    totalAmount: 0,
    monthlyAmount: 0,
    yearlyAmount: 0,
    manualAmount: 0,
  });

  // User tab pagination
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);
  const [userTotalCount, setUserTotalCount] = useState(0);

  const handleApplyFilters = (values) => {
    setFilters(values);
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      preset: "all",
      startDate: null,
      endDate: null,
      planType: "all",
      // userOption: null,
    });
  };

  // Report tab handlers
  const handleChangeReportPage = (_event, newPage) => {
    setReportPage(newPage);
  };

  const handleChangeReportRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setReportRowsPerPage(next);
    setReportPage(0);
  };

  // User tab handlers
  const handleChangeUserPage = (_event, newPage) => {
    setUserPage(newPage);
  };

  const handleChangeUserRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setUserRowsPerPage(next);
    setUserPage(0);
  };

  const handleReportLoaded = useCallback(({ total, totals }) => {
    setReportTotalCount(total);
    if (totals) {
      setReportTotals({
        totalAmount: totals.totalAmount ?? 0,
        monthlyAmount: totals.monthlyAmount ?? 0,
        yearlyAmount: totals.yearlyAmount ?? 0,
        manualAmount: totals.manualAmount ?? 0,
      });
    }
  }, []);

  const handleUserLoaded = useCallback(({ total, totals }) => {
    setUserTotalCount(total);
  }, []);

  return (
    <>
      <Helmet>
        <title>Ove | Admin Subscription</title>
      </Helmet>
      <div className="page-wrapper subscription-container">
        <div className="content container-fluid">
          <div className="admin-wrapper subscription-table">
            <div className="subscription-tabs mb-3">
              <button
                type="button"
                className={`subscription-tab-btn ${type === "report" ? "active" : ""}`}
                onClick={() => {
                  if (type !== "report") {
                    setType("report");
                    setFilters(defaultFilters.current);
                    setReportPage(0);
                    setUserPage(0);
                    setIsFilterOpen(false);
                  }
                }}
              >
                Report
              </button>
              <button
                type="button"
                className={`subscription-tab-btn ${type === "user" ? "active" : ""}`}
                onClick={() => {
                  if (type !== "user") {
                    setType("user");
                    setFilters(defaultFilters.current);
                    setReportPage(0);
                    setUserPage(0);
                    setIsFilterOpen(false);
                  }
                }}
              >
                User
              </button>
            </div>

            {type === "report" && (
              <div className="row subscription-stats-row">
                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content">
                      <div className="subscription-stats-title">Total Amount</div>
                      <div className="subscription-stats-value">£{reportTotals.totalAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaPoundSign />
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content">
                      <div className="subscription-stats-title">Monthly</div>
                      <div className="subscription-stats-value">£{reportTotals.monthlyAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaCalendarAlt />
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content ">
                      <div className="subscription-stats-title">Yearly</div>
                      <div className="subscription-stats-value">£{reportTotals.yearlyAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaCalendarCheck />
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content">
                      <div className="subscription-stats-title">Manual</div>
                      <div className="subscription-stats-value">£0</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaUserEdit />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-12">
                <div>
                  <div className="subscription-table-header">
                    <div className="subscription-table-actions">
                      <div className="subscription-action-buttons">
                        {type === "report" && (
                          <ExportButton
                            moduleName="subscription-report"
                            apiService={() => GetSubscriptionReports({ page: 1, limit: 10, isExport: true })}
                            data={{}}
                            className="primary-button action-btn"
                            disabled={reportTotalCount === 0}
                          />
                        )}
                        <button
                          type="button"
                          className={`subscription-filter-trigger action-btn ${isFilterOpen ? 'active' : ''}`}
                          onClick={() => setIsFilterOpen(true)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                            <line x1="4" y1="6" x2="20" y2="6"></line>
                            <line x1="4" y1="12" x2="20" y2="12"></line>
                            <line x1="4" y1="18" x2="20" y2="18"></line>
                          </svg>
                          Filters
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    {type === "report" && (
                      <>
                        <ReportTab
                          filters={filters}
                          page={reportPage}
                          rowsPerPage={reportRowsPerPage}
                          onLoaded={handleReportLoaded}
                        />
                        <Pagination
                          totalItem={reportTotalCount}
                          itemsPerPage={reportRowsPerPage}
                          page={reportPage}
                          handleChangePage={handleChangeReportPage}
                          handleChangeRowsPerPage={handleChangeReportRowsPerPage}
                        />
                      </>
                    )}
                    {type === "user" && (
                      <>
                        <UserTab
                          filters={filters}
                          page={userPage}
                          rowsPerPage={userRowsPerPage}
                          onLoaded={handleUserLoaded}
                        />
                        <Pagination
                          totalItem={userTotalCount}
                          itemsPerPage={userRowsPerPage}
                          page={userPage}
                          handleChangePage={handleChangeUserPage}
                          handleChangeRowsPerPage={handleChangeUserRowsPerPage}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <FilterSidebar
              open={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              initialFilters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              moduleType="subscription"
              setPage={() => { setReportPage(0); setUserPage(0); }}
              tabType={type}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Subscription;

